<template>
  <div>
    <b-row class="align-items-center mb-2">
      <b-col md="6" class="mb-2">
        <h5 class="subTit">{{ $route.meta.breadcrumb[1].text }} List</h5>
      </b-col>
      <b-col md="6" class="mb-2 d-flex justify-content-md-end align-items-center">
        <div class="search-input-wrapper mr-2">
          <feather-icon icon="SearchIcon" size="15" class="search-icon" />
          <b-form-input
            v-model="searchQuery"
            placeholder="Search product name..."
            size="sm"
            class="search-input"
          />
        </div>
        <div class="view-toggle" role="group" aria-label="Product view mode">
          <b-button
            variant="link"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'grid' }"
            :pressed="viewMode === 'grid'"
            title="Grid view"
            @click="setViewMode('grid')"
          >
            <feather-icon icon="GridIcon" size="15" />
          </b-button>
          <b-button
            variant="link"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'list' }"
            :pressed="viewMode === 'list'"
            title="List view"
            @click="setViewMode('list')"
          >
            <feather-icon icon="ListIcon" size="15" />
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-for="val in displayedProductArr"
        :key="val.__list_key || val.KODE"
        cols="12"
        :sm="viewMode === 'grid' ? 6 : 12"
        :md="viewMode === 'grid' ? 4 : 12"
        :lg="viewMode === 'grid' ? 3 : 12"
        :class="['d-flex', 'flex-column', viewMode === 'list' ? 'mb-1' : 'mb-2']"
      >
        <ProductCard
          :item="val"
          :context="$route.meta.breadcrumb[1].text"
          :view-mode="viewMode"
        />
      </b-col>

      <template v-if="loading && !displayedProductArr.length">
        <b-col
          v-for="i in 5"
          :key="'skl-' + i"
          cols="12"
          :sm="viewMode === 'grid' ? 6 : 12"
          :md="viewMode === 'grid' ? 4 : 12"
          :lg="viewMode === 'grid' ? 3 : 12"
        >
          <ProductCardSkeleton with-badge />
        </b-col>
      </template>
      <b-col v-if="!displayedProductArr.length && !loading" class="text-center">
        <b-card>
          <b-card-text>
            <h3 class="text-muted">
              No Data, please select another Product CFU
            </h3>
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>

    <div ref="infiniteScrollSentinel" class="infinite-scroll-sentinel" />
    <b-row v-if="loading && displayedProductArr.length" class="justify-content-center mt-1">
      <b-spinner small variant="danger" />
    </b-row>
    <b-row v-if="!hasMore && displayedProductArr.length" class="justify-content-center mt-2 mb-1">
      <span class="text-muted small">Showing all {{ displayedProductArr.length }} products</span>
    </b-row>
  </div>
</template>

<script>
import {
  BSpinner,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardText,
  BButton,
  BFormInput,
} from 'bootstrap-vue'
import debounce from 'lodash/debounce'
import ProductCard from '@/views/user/product/components/ProductBookmarkCard.vue'
import ProductCardSkeleton from '@/views/user/product/components/ProductCardSkeleton.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  components: {
    BSpinner,
    BRow,
    BCol,
    BCard,
    BCardText,
    BCardBody,
    BButton,
    BFormInput,
    ProductCard,
    ProductCardSkeleton,
    FeatherIcon,
  },
  data() {
    return {
      banner: require('@/assets/images/product_banner.png'),
      icons: ['ChevronRightIcon'],
      productArr: [],
      categories: null,
      ebis: '',
      wins: '',
      cons: '',
      loading: false,
      currentPage: 1,
      perPage: 12,
      hasMore: true,
      searchQuery: '',
      viewMode: 'grid',
      totalRows: 0,
      observer: null,
    }
  },
  computed: {
    displayedProductArr() {
      const normalizedRoots = (this.productArr || []).map(node =>
        this.normalizeNode(node)
      )
      return this.flattenProductTree(normalizedRoots)
    },
  },
  watch: {
    searchQuery: debounce(function() {
      this.productArr = []
      this.currentPage = 1
      this.hasMore = true
      this.fetchProduct()
    }, 400),
    $route(to, from) {
      this.productArr = []
      this.currentPage = 1
      this.totalRows = 0
      this.hasMore = true
      this.searchQuery = ''
      if (this.$route.meta.breadcrumb[1].text == 'CONS') {
        this.categories = 1
      } else if (this.$route.meta.breadcrumb[1].text == 'EBIS') {
        this.categories = 2
      } else if (this.$route.meta.breadcrumb[1].text == 'WIBS') {
        this.categories = 3
      } else {
        this.categories = 4
      }
      this.fetchProduct()
    },
  },
  created() {
    this.fetchProductLabel()
    if (this.$route.meta.breadcrumb[1].text == 'CONS') {
      this.categories = 1
    } else if (this.$route.meta.breadcrumb[1].text == 'EBIS') {
      this.categories = 2
    } else if (this.$route.meta.breadcrumb[1].text == 'WIBS') {
      this.categories = 3
    } else {
      this.categories = 4
    }
    this.fetchProduct()
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !this.loading && this.hasMore) {
          this.loadNextPage()
        }
      },
      { threshold: 0.1 }
    )
    if (this.$refs.infiniteScrollSentinel) {
      this.observer.observe(this.$refs.infiniteScrollSentinel)
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    setViewMode(mode) {
      this.viewMode = mode === 'list' ? 'list' : 'grid'
    },
    getNodeChildren(node) {
      if (!node) return []
      if (Array.isArray(node.child_details) && node.child_details.length)
        return node.child_details
      if (Array.isArray(node.CHILD) && node.CHILD.length) return node.CHILD
      return []
    },
    normalizeNode(node) {
      if (!node || typeof node !== 'object') return {}
      const children = this.getNodeChildren(node)
      const normalizedChildren = children.map(child =>
        this.normalizeNode(child)
      )
      return {
        ...node,
        child_details: normalizedChildren,
      }
    },
    flattenProductTree(nodes, depth = 0, parentKey = 'root', acc = []) {
      if (!Array.isArray(nodes) || !nodes.length) return acc
      nodes.forEach((node, index) => {
        if (!node) return
        const keySeed = node.KODE || node.PRODUCT || index
        const listKey = `${parentKey}-${depth}-${keySeed}-${index}`
        acc.push({
          ...node,
          __list_key: listKey,
        })
        const children = this.getNodeChildren(node)
        if (children.length) {
          this.flattenProductTree(
            children,
            depth + 1,
            `${parentKey}-${keySeed}-${index}`,
            acc
          )
        }
      })
      return acc
    },
    fetchProductLabel() {
      this.$http
        .get('/settings/product')
        .then(res => {
          this.ebis = res.data.data.ebis
          this.wins = res.data.data.wins
          this.cons = res.data.data.cons
        })
        .catch(err => {
          this.$notif('Error', `${err.message}`, 'error')
        })
    },
    loadNextPage() {
      if (this.loading || !this.hasMore) return
      this.fetchProduct()
    },
    fetchProduct() {
      const mapRequest = {
        map: {
          KODE: 's',
          PRODUCT: 's',
          LAST_UPDATE: 'd',
          SHORT_DESCRIPTION: 'd',
          child_details: 'd',
          PROD_CATEGORY: 'd',
          product_category_details: 'd',
          photo_product: 'd',
          cfu_business_details: 'd',
          cfu_product_details: 'd',
          is_bookmarked: 'd',
        },
      }
      const skip = (this.currentPage - 1) * this.perPage
      const take = this.perPage
      const keyword = this.searchQuery.trim()
      const searchSegment = keyword ? `/s/${encodeURIComponent(keyword)}` : ''
      this.loading = true
      this.$http
        .post(
          `/product/browse/take/${take}/skip/${skip}/cfu/${this.categories}/orderBy.PRODUCT/asc${searchSegment}/published/true/root/true`,
          mapRequest
        )
        .then(response => {
          const rows = response.data.data.records || []
          this.productArr = [...this.productArr, ...rows]
          if (rows.length < take) {
            this.hasMore = false
          } else {
            this.currentPage += 1
          }
          const rawTotal =
            response?.data?.data?.total ??
            response?.data?.data?.total_rows ??
            response?.data?.data?.totalData ??
            response?.data?.data?.count
          const parsedTotal = Number(rawTotal)
          if (Number.isFinite(parsedTotal) && parsedTotal >= 0) {
            this.totalRows = parsedTotal
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.subTit {
  font-weight: bold;
}

.view-toggle {
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}

.view-toggle-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 999px;
  color: #64748b !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none !important;
}

.view-toggle-btn:hover {
  background: rgba(148, 163, 184, 0.18);
  color: #334155 !important;
}

.view-toggle-btn.active {
  background: #ef4444;
  color: #fff !important;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.35);
}

.infinite-scroll-sentinel {
  height: 1px;
  width: 100%;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 280px;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  z-index: 1;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 40px;
  padding-left: 36px !important;
  padding-right: 12px;
  border-radius: 999px !important;
  border: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  font-size: 0.875rem;
}

.search-input:focus {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}
</style>
